import React, { useState } from "react";
import { Box, Typography, TextField, Button, Alert, Card, CardContent } from "@mui/material";
import { isUserAvailable } from "../services/userAuthService";

interface AuthInterfaceProps {
  onAuthenticated: (email: string) => void;
}

const AuthInterface: React.FC<AuthInterfaceProps> = ({ onAuthenticated }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async () => {
    setErr(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setErr("Please enter your email.");
      return;
    }

    setLoading(true);
    try {
      const ok = await isUserAvailable(trimmed);
      if (!ok) {
        setErr("Access denied. This email is not allowed.");
        return;
      }

      // Save session locally
      localStorage.setItem("pos_user_email", trimmed);
      localStorage.setItem("pos_authed", "true");

      onAuthenticated(trimmed);
    } catch (e: any) {
      setErr(e?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Card sx={{ width: "100%", maxWidth: 520 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" sx={{ color: "#2C507A", fontWeight: 700, mb: 2 }}>
            Sign in to continue
          </Typography>
          <Typography sx={{ color: "#6C757D", mb: 3 }}>
            Enter your email to access the POS.
          </Typography>

          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />

          {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ mt: 3, width: "100%", backgroundColor: "#2C507A", "&:hover": { backgroundColor: "#1E3A5F" } }}
          >
            {loading ? "Checking..." : "Continue"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthInterface;
